import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { usePageBuilder } from '../../hooks/usePageBuilder';
import { useAccordionState } from '../../hooks/useAccordionState';
import { useAppStore } from '../../store/useAppStore';
import { exportPageConfig } from '../../utils/exportConfig';
import { updatePage, saveComponentToLibrary, generateComponentId } from '../../services/firestoreService';
import { handleReturn, hasReturnUrl } from '../../utils/navigationHelper';
import PageSelector from './PageSelector';
import LeftPanel from './LeftPanel';
import RightPanel from './RightPanel';
import FormBuilder from '../FormBuilder/FormBuilder';
import CardTemplateModal from './CardTemplateModal';
import { getCardTemplate } from '../../config/cardTemplates';

const PageBuilder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pageBuilderHook = usePageBuilder();
  const accordionHook = useAccordionState();
  const pendingCardCreateRef = useRef(null);
  const pendingFormCreateRef = useRef(null);
  const { appName, setAppName } = useAppStore();
  const [pageName, setPageName] = useState(''); // Start with empty string to always prompt for page name
  const [showPageNameDialog, setShowPageNameDialog] = useState(false);
  const [currentPageId, setCurrentPageId] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [formSlotId, setFormSlotId] = useState(null); // Track selected slot for form
  const [componentRefreshTrigger, setComponentRefreshTrigger] = useState(0); // Event-driven refresh trigger
  const [showCardTemplateModal, setShowCardTemplateModal] = useState(false);
  const [cardTemplateSlotId, setCardTemplateSlotId] = useState(null);

  // FIXED by Claude on 2025-11-16 - Read appName from URL and set to store
  // This ensures pages are fetched for the correct app
  useEffect(() => {
    const appFromUrl = searchParams.get('app');
    if (appFromUrl && appFromUrl !== appName) {
      console.log(`🔗 [PageBuilder] Setting app from URL: ${appFromUrl}`);
      setAppName(appFromUrl);
    } else if (!appFromUrl && !appName) {
      console.warn('⚠️ [PageBuilder] No app specified in URL or store');
    }
  }, [searchParams, appName, setAppName]);

  useEffect(() => {
    const handlePreviewRequest = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'REQUEST_PREVIEW_DATA') return;
      const targetWindow = event.source;
      if (!targetWindow) return;
      try {
        targetWindow.postMessage({
          type: 'UPDATE_PREVIEW',
          pageData: pageBuilderHook.pageData
        }, window.location.origin);
        console.log('📡 [PageBuilder] Sent page data to preview window');
      } catch (error) {
        console.warn('⚠️ [PageBuilder] Failed to send preview data', error);
      }
    };

    window.addEventListener('message', handlePreviewRequest);
    return () => window.removeEventListener('message', handlePreviewRequest);
  }, [pageBuilderHook.pageData]);

  // Handle loading existing page
  const handlePageLoad = (pageData) => {
    if (!pageData) {
      // New page - reset
      pageBuilderHook.setPageData({
        layoutId: 'single-column',
        themeId: 'modern',
        components: [],
        metadata: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: '1.0.0'
        }
      });
      setCurrentPageId(null);
      setPageName('');
      return;
    }

    // Load existing page
    pageBuilderHook.setPageData({
      layoutId: pageData.layoutId || 'single-column',
      themeId: pageData.themeId || 'modern',
      components: pageData.components || [],
      metadata: pageData.metadata || {}
    });
    setCurrentPageId(pageData.id);
    setPageName(pageData.pageName || pageData.name || '');
  };

  // Handle adding component from library
  // Enhanced by Claude on 2025-11-23 - Ensure complete component JSON is used
  const handleAddFromLibrary = (libraryComponent) => {
    console.group('📦 [PageBuilder] Adding Component from Library');
    console.log('Library Component Data:', libraryComponent);
    console.log('Component Type:', libraryComponent.type);
    console.log('Has specificAttrs:', !!libraryComponent.specificAttrs);
    console.log('Has commonAttrs:', !!libraryComponent.commonAttrs);
    
    // Create a copy with new ID while preserving ALL component data
    const newId = generateComponentId(
      pageName || 'page',
      libraryComponent.type,
      pageBuilderHook.pageData.components
    );

    // Spread operator preserves all nested objects (specificAttrs, commonAttrs, fields, actions, etc.)
    const newComponent = {
      ...libraryComponent,
      id: newId,
      sourceComponentId: libraryComponent.id,  // Track original component for reference
      addedFrom: 'library',
      addedAt: new Date().toISOString(),
      createdAt: libraryComponent.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('New Component with ID:', newId);
    console.log('Complete Component Data:', newComponent);
    console.groupEnd();

    pageBuilderHook.setPageData(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));

    accordionHook.expandAccordion(newId);
    alert(`✅ Added ${libraryComponent.name || libraryComponent.type} from library with all data preserved!`);
  };

  // Handle adding new component
  const handleAddNewComponent = (componentType, componentName, slotId = null) => {
    // Default: regular component
    const newId = pageBuilderHook.addComponent(componentType, slotId);

    // Update with name if provided
    if (componentName) {
      pageBuilderHook.updateComponent(newId, {
        name: componentName
      });
    }

    accordionHook.expandAccordion(newId);
    return newId;
  };

  // Handle adding card component (special case: template picker)
  const handleAddCardComponent = (slotId = null, options = {}) => {
    pendingCardCreateRef.current = {
    slotId,
    onCreated: typeof options?.onCreated === 'function' ? options.onCreated : null
  };
    setCardTemplateSlotId(slotId);
    setShowCardTemplateModal(true);
  };

  // Handle card template selection
  const handleCardTemplateSelect = (templateKey) => {
    setShowCardTemplateModal(false);

    // Blank card: just add the default card component
    if (templateKey === '__blank__') {
      const newId = pageBuilderHook.addComponent('card', cardTemplateSlotId);
      accordionHook.expandAccordion(newId);
      try {
    pendingCardCreateRef.current?.onCreated?.(newId);
  } catch (e) {
    console.warn('[PageBuilder] card onCreated callback failed', e);
  }
  pendingCardCreateRef.current = null;
      setCardTemplateSlotId(null);
      return;
    }

    const template = getCardTemplate(templateKey);
    const newId = pageBuilderHook.addComponent('card', cardTemplateSlotId);

    pageBuilderHook.updateComponent(newId, {
      type: 'card',
      name: template.name || 'Card Template',
      description: template.description || '',
      commonAttrs: template.commonAttrs,
      specificAttrs: template.specificAttrs,
      validations: template.validations || [],
      actions: template.actions || [],
      templateKey,
      templateName: template.name || templateKey,
      createdFrom: 'template',
      updatedAt: new Date().toISOString()
    });

    accordionHook.expandAccordion(newId);
    try {
  pendingCardCreateRef.current?.onCreated?.(newId);
} catch (e) {
  console.warn('[PageBuilder] card onCreated callback failed', e);
}
pendingCardCreateRef.current = null;
    setCardTemplateSlotId(null);
  };

  // Handle adding form component (special case)
  const handleAddFormComponent = (slotId = null) => {
    console.log('PageBuilder: handleAddFormComponent called', { slotId });
    setFormSlotId(slotId);
    setShowFormBuilder(true);
    console.log('PageBuilder: showFormBuilder set to true, slotId:', slotId);
  };

  // Handle form builder close
  const handleFormBuilderClose = () => {
    setShowFormBuilder(false);
    setFormSlotId(null);
  };

  /**
   * Handle form save from FormBuilder
   * 
   * What this does:
   * 1. Creates a new form component with unique ID
   * 2. Adds it to the page's components array
   * 3. ALSO saves it to {appName}_components collection in JAYRAM (for reusability)
   * 
   * This allows the form to be:
   * - Part of the current page
   * - Available in component library for other pages
   */
  const handleFormSave = async (formData) => {
    console.group('📋 [PageBuilder] Saving Form Component');
    console.log('Form Data:', formData);
    
    // Generate unique component ID
    const newId = generateComponentId(
      pageName || 'page',
      'form',
      pageBuilderHook.pageData.components
    );

    // Create component with the structure expected by the existing system
    const newComponent = {
      id: newId,
      type: 'form',
      name: formData.title || 'Form Component',
      appName: appName,  // Track which app this component belongs to
      slotId: formSlotId, // Assign to the selected slot
      
      // Add commonAttrs in FLAT structure (consistent with other components)
      commonAttrs: {
        width: '100',
        height: 'auto',
        paddingTop: '16',
        paddingBottom: '16',
        paddingLeft: '16',
        paddingRight: '16',
        marginTop: '0',
        marginBottom: '16',
        marginLeft: '0',
        marginRight: '0',
        backgroundColor: 'transparent',
        borderRadius: '8',
        boxShadow: 'none'
      },
      
      // Store form-specific data in specificAttrs
      specificAttrs: {
        formType: formData.formType,
        title: formData.title,
        description: formData.description,
        submitLabel: formData.submitLabel,
        fields: formData.fields,
        ui: formData.ui,
        actions: formData.actions || []  // Form actions (validate, save, etc.)
      },
      
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    console.log('Created form component structure:', newComponent);

    // Step 1: Add form to current page's components array
    pageBuilderHook.setPageData(prev => ({
      ...prev,
      components: [...prev.components, newComponent]
    }));
    console.log('✅ [PageBuilder] Form added to page components');
    pendingFormCreateRef.current?.onCreated?.(newId);
    pendingFormCreateRef.current = null;

    // Step 2: Save form component to {appName}_components collection in JAYRAM
    // This makes it reusable across other pages
    // Enhanced by Claude on 2025-11-23 - Added page reference tracking
    try {
      console.log(`💾 [PageBuilder] Saving form to component library: ${appName}_components`);
      const saveResult = await saveComponentToLibrary(appName, {
        ...newComponent,
        pageName: pageName || 'page',
        pageId: currentPageId || pageName || 'page',
        pageReference: currentPageId ? `${currentPageId}_${formData.title}` : null,
        tags: ['form', formData.formType, 'user-created'],  // Add tags for searchability
        category: 'forms',
        createdBy: 'user',
        updatedBy: 'user',
        savedAt: new Date().toISOString()
      });
      
      if (saveResult) {
        console.log(`✅ [PageBuilder] Form saved to JAYRAM: ${appName}_components/${newId}`);
        // Trigger event-driven refresh of component list
        setComponentRefreshTrigger(prev => prev + 1);
        console.log('🔄 [PageBuilder] Triggered component list refresh after form save');
      } else {
        console.warn('⚠️ [PageBuilder] Form added to page but not saved to library');
      }
    } catch (error) {
      console.error('❌ [PageBuilder] Failed to save form to component library:', error);
      // Don't block the form from being added to page even if library save fails
    }

    console.groupEnd();

    // Expand the newly created form in the accordion
    accordionHook.expandAccordion(newId);
    
    // Close the Form Builder modal and reset slotId
    setShowFormBuilder(false);
    setFormSlotId(null);
    
    // Show success message
    const slotInfo = formSlotId ? ` in slot "${formSlotId}"` : '';
    alert(`✅ Form "${formData.title}" created and added to page${slotInfo}!\n\n` +
          `The form is now:\n` +
          `• Part of this page${slotInfo}\n` +
          `• Saved to component library for reuse\n\n` +
          `Don't forget to save the page to persist all changes!`);
  };

  // Handle saving component to library
  // Enhanced by Claude on 2025-11-23 - Save component with page reference
  const handleSaveToLibrary = async (component) => {
    if (!component.name) {
      const name = prompt('Enter a name for this component:', `${component.type}_component`);
      if (!name) return;
      component = { ...component, name };
    }

    try {
      // Save the component to appname_Components collection
      const componentToSave = {
        ...component,
        pageName: pageName || 'page',
        pageId: currentPageId || pageName || 'page',  // Track which page it belongs to
        pageReference: currentPageId ? `${currentPageId}_${component.name || component.type}` : null,  // Page-specific reference
        createdBy: 'user',
        updatedBy: 'user',
        savedAt: new Date().toISOString()
      };

      console.log(`💾 [PageBuilder] Saving component to library: ${component.name}`, {
        componentId: component.id,
        pageName: pageName,
        pageId: currentPageId,
        pageReference: componentToSave.pageReference
      });

      const success = await saveComponentToLibrary(appName, componentToSave);

      if (success) {
        // Trigger event-driven refresh of component list
        setComponentRefreshTrigger(prev => prev + 1);
        console.log('🔄 [PageBuilder] Triggered component list refresh');
        
        alert(
          `✅ ${component.name} saved to library!\n\n` +
          `The component is now:\n` +
          `• Available for reuse in any page\n` +
          `• Linked to page: ${pageName || 'current page'}\n` +
          (componentToSave.pageReference ? `• Reference: ${componentToSave.pageReference}` : '')
        );
      } else {
        alert('❌ Failed to save to library');
      }
    } catch (error) {
      console.error('❌ [PageBuilder] Failed to save component to library:', error);
      alert('❌ Failed to save to library: ' + error.message);
    }
  };

  const handleExport = () => {
    try {
      exportPageConfig(pageBuilderHook.pageData);
      alert('✅ Configuration exported successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('❌ Export failed. Check console for details.');
    }
  };

  const handleSaveClick = () => {
    // Always show dialog if no pageName or if we want to save as new page
    // Only skip dialog if we're updating an existing page (has currentPageId AND pageName)
    if (!pageName || !currentPageId) {
      setShowPageNameDialog(true);
    } else {
      // For existing pages, ask if they want to update or save as new
      const updateOrNew = window.confirm(
        `Page "${pageName}" already exists.\n\n` +
        `OK - Update existing page\n` +
        `Cancel - Save as new page (will prompt for new name)`
      );
      
      if (updateOrNew) {
        // Update existing page
        handleSavePage();
      } else {
        // Save as new - clear currentPageId and show dialog
        setCurrentPageId(null);
        setPageName('');
        setShowPageNameDialog(true);
      }
    }
  };

  /**
   * Save page to JAYRAM database
   * 
   * Flow:
   * 1. Validates page name
   * 2. Constructs page data with metadata
   * 3. Calls API to save to {appName}_pages collection in JAYRAM
   * 
   * IMPORTANT: pageName is used as-is (not prefixed with appName)
   * Backend handles collection naming: {appName}_pages
   */
  const handleSavePage = async () => {
    if (!pageName.trim()) {
      alert('❌ Please enter a page name');
      return;
    }

    try {
      // Use currentPageId only if we're updating an existing page
      // For new pages, use the pageName as the document ID
      const pageDocName = currentPageId || pageName.trim();
      
      const pageJSON = {
        ...pageBuilderHook.pageData,
        appName,           // Which app this page belongs to
        pageName: pageName.trim(),          // Page name (e.g., "HomePage", "ContactPage")
        docName: pageDocName,
        savedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        updatedBy: 'user'
      };

      console.group('💾 [PageBuilder] Saving Page to JAYRAM Database');
      console.log('App Name:', appName);
      console.log('Page Name:', pageName.trim());
      console.log('Document ID:', pageDocName);
      console.log('Is Update:', !!currentPageId);
      console.log('Collection:', `${appName}_pages`);
      console.log('Components Count:', pageJSON.components?.length || 0);
      console.groupEnd();

      // Save to JAYRAM via API: Backend saves to {appName}_pages collection
      const success = await updatePage(appName, pageDocName, pageJSON);
      
      if (success) {
        console.log(`✅ [PageBuilder] Page saved to JAYRAM: ${appName}_pages/${pageDocName}`);
        setShowPageNameDialog(false);
        // Update currentPageId to the saved page ID so future saves will update this page
        setCurrentPageId(pageDocName);
        
        // Check if we need to return to a previous page (from Process Builder)
        if (hasReturnUrl(searchParams)) {
          alert(`✅ Page "${pageName.trim()}" saved! Returning to Process Builder...`);
          setTimeout(() => {
            handleReturn(navigate, searchParams, '/dashboard');
          }, 500);
        } else {
          alert(`✅ Page "${pageName.trim()}" saved successfully to JAYRAM database!`);
        }
      } else {
        alert('❌ Failed to save page. Check console for details.');
      }
    } catch (error) {
      console.error('❌ [PageBuilder] Save page error:', error);
      alert('❌ Save failed. Check console for details.');
    }
  };

  return (
    <>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Unified Header with MUDUMBAI logo, page selector, and profile icon */}
        <PageSelector onPageLoad={handlePageLoad} />

        {/* Main Builder */}
        <div className="page-builder" style={{ flex: 1 }}>
          <LeftPanel 
            pageData={pageBuilderHook.pageData}
            updateLayout={pageBuilderHook.updateLayout}
            updateTheme={pageBuilderHook.updateTheme}
            onAddFromLibrary={handleAddFromLibrary}
            onAddNewComponent={handleAddNewComponent}
            onAddFormComponent={handleAddFormComponent}
            onAddCardComponent={handleAddCardComponent}
            updateComponent={pageBuilderHook.updateComponent}
            deleteComponent={pageBuilderHook.deleteComponent}
            duplicateComponent={pageBuilderHook.duplicateComponent}
            accordionState={accordionHook}
            onSaveToLibrary={handleSaveToLibrary}
            componentRefreshTrigger={componentRefreshTrigger}
          />
          <RightPanel 
            pageData={pageBuilderHook.pageData}
            updateComponent={pageBuilderHook.updateComponent}
            onComponentClick={(componentId) => accordionHook.expandAccordion(componentId)}
            selectedComponentId={accordionHook.expandedId}
            onExport={handleExport}
            onSave={handleSaveClick}
          />
        </div>
      </div>

      {/* Page Name Dialog */}
      {showPageNameDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
              Save Page
            </h3>
            <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6B7280' }}>
              Enter a name for this page
            </p>
            <input
              type="text"
              value={pageName}
              onChange={(e) => setPageName(e.target.value)}
              placeholder="e.g., home, about, contact"
              autoFocus
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '14px',
                border: '1px solid #D1D5DB',
                borderRadius: '8px',
                marginBottom: '16px'
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSavePage();
                }
              }}
            />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPageNameDialog(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePage}
                className="btn-primary"
              >
                Save Page
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form Builder Modal */}
      {showFormBuilder && (
        <>
          {console.log('PageBuilder: Rendering FormBuilder component')}
          <FormBuilder
            onClose={handleFormBuilderClose}
            onSave={handleFormSave}
          />
        </>
      )}

      {/* Card Template Picker Modal */}
      {showCardTemplateModal && (
        <CardTemplateModal
          isOpen={showCardTemplateModal}
          onClose={() => { pendingCardCreateRef.current = null; setShowCardTemplateModal(false); setCardTemplateSlotId(null); }}
          onSelectTemplate={handleCardTemplateSelect}
        />
      )}
    </>
  );
};

export default PageBuilder;
